// @flow

import React from 'react'

import Loading from '../../Icons/loading.gif'
import { Chart as ChartJS, BarController, BarElement, Tooltip, Legend, LinearScale, CategoryScale } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import ChartjsPluginStacked100 from 'chartjs-plugin-stacked100'
import moment from 'moment'
import { Link } from 'react-router-dom'

ChartJS.register(
  ChartjsPluginStacked100,
  BarController,
  BarElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale
)

const tooltipPlugin = ChartJS.registry.getPlugin('tooltip')
tooltipPlugin.positioners.center = function (elements) {
  if (elements[0]) {
    const { x, y, base } = elements[0].element
    const width = base - x
    return { x: x + (width / 2), y }
  }
  return 'average'
}

const options = {
  indexAxis: 'y',
  plugins: {
    tooltip: {
      position: 'center',
      callbacks: {
        label: (tooltipItem) => {
          const data = tooltipItem.chart.data
          const datasetIndex = tooltipItem.datasetIndex
          const index = tooltipItem.dataIndex
          const datasetLabel = data.datasets[datasetIndex].label || ''
          const originalValue = data.originalData[datasetIndex][index]
          return `${datasetLabel}: ${originalValue}`
        }
      }
    },
    stacked100: { enable: true, replaceTooltipLabel: false }
  },
  scales: {
    x: {
      stacked: true,
      grid: {
        display: false
      },
      display: false
    },
    y: {
      stacked: true,
      grid: {
        display: false
      }
    }
  }
}

type Props = {
  monthlyActiveUsers: Array,
  loading: boolean
}

function MonthlyActiveUsers ({ monthlyActiveUsers, loading }: Props) {
  const LoadingIndicator = () => (<div><img src={Loading} alt='Loading' /></div>)

  const buildData = (monthlyActiveUsers) => {
    const mauFormatted = []
    for (const i in monthlyActiveUsers) {
      const data = monthlyActiveUsers[i]
      mauFormatted[data.month] = data
    }

    const counts = {
      unregisteredCounts: [],
      inactiveCounts: [],
      activeCounts: []
    }

    const startMonth = moment().subtract(11, 'month').startOf('month')
    const startMonthIndex = startMonth.format('M')
    const startMonthYear = parseInt(startMonth.format('YYYY'))

    if (mauFormatted[startMonthIndex] !== undefined) {
      const startMonthData = mauFormatted[startMonthIndex]
      if (startMonthData.year === startMonthYear) {
        counts.unregisteredCounts.push(startMonthData.unregisteredCount)
        counts.inactiveCounts.push(startMonthData.inactiveCount)
        counts.activeCounts.push(startMonthData.activeCount)
      }
    } else {
      counts.unregisteredCounts.push(0)
      counts.inactiveCounts.push(0)
      counts.activeCounts.push(0)
    }

    const months = [startMonth.format('MMMM')]
    for (let i = 0; i < 11; i++) {
      const nextMonth = startMonth.add(1, 'M')
      const nextMonthIndex = nextMonth.format('M')
      const nextMonthYear = parseInt(nextMonth.format('YYYY'))

      if (mauFormatted[nextMonthIndex] !== undefined) {
        const nextMonthData = mauFormatted[nextMonthIndex]
        if (nextMonthData.year === nextMonthYear) {
          counts.unregisteredCounts.push(nextMonthData.unregisteredCount)
          counts.inactiveCounts.push(nextMonthData.inactiveCount)
          counts.activeCounts.push(nextMonthData.activeCount)
        }
      } else {
        counts.unregisteredCounts.push(0)
        counts.inactiveCounts.push(0)
        counts.activeCounts.push(0)
      }

      months.push(nextMonth.format('MMMM'))
    }

    return {
      labels: months,
      datasets: [
        {
          label: 'Not Registered',
          data: counts.unregisteredCounts,
          borderWidth: 1,
          backgroundColor: '#F07B7B'
        },
        {
          label: 'Inactive',
          data: counts.inactiveCounts,
          borderWidth: 1,
          backgroundColor: '#ECB33D'
        },
        {
          label: 'Active',
          data: counts.activeCounts,
          borderWidth: 1,
          backgroundColor: '#0ABD63'
        }
      ]
    }
  }

  const data = buildData(monthlyActiveUsers)

  return (
    <div>
      <h3 style={{ float: 'left' }}>MONTHLY ACTIVE USERS</h3>
      <span style={{ float: 'right' }}>
        <Link to={{ pathname: '/user-activity' }}>
          <h3 className='HeaderLink'>VIEW IN USER ACTIVITY PAGE</h3>
        </Link>
      </span>
      {loading && <LoadingIndicator />}
      {!loading && (
        <Bar data={data} options={options} height={100} />
      )}
    </div>
  )
}

export default MonthlyActiveUsers
